import type { QueryFunction, QueryKey } from '@tanstack/react-query'

import type {
  ServiceGetAllResponse,
  ServiceGetByIdResponse,
  ServiceMutationOptionType,
  ServiceQueryOptionType
} from '@/types/service'

import { api, handleAxiosError } from '@/lib/axios'

import type {
  KomoditasPetaniDataType,
  PetaniDataType
} from '../daftar-petani/types'
import type { KomoditasType } from '../references/types'
import type {
  ExportResponse,
  JenisKelompokPoktanDataType,
  KelasKemampuanDataType,
  KomoditasAnggotaPoktanItemType,
  KomoditasPoktanDataType,
  PoktanAnggotaDataType,
  PoktanDataType,
  RiwayatKodePoktanDataType
} from './types'
import type {
  AddComodityMemberValues,
  AddComodityPoktanValues,
  CreateAbilityClassValues,
  FilterPoktanValues,
  MutationAnggotaPoktanValues,
  MutationPoktanValues
} from './validation'

const POKTAN_QUERY_KEY = (key?: string | Record<string, any>) =>
  ['poktan', key] as const

interface InfiniteQueryOptions {
  queryKey: QueryKey
  initialPageParam: number
  getNextPageParam: (
    lastPage: ServiceGetAllResponse<PoktanDataType[]>
  ) => number | undefined
  queryFn: QueryFunction<
    ServiceGetAllResponse<PoktanDataType[]>,
    QueryKey,
    number
  >
}

const getAll = (
  params?: FilterPoktanValues
): ServiceQueryOptionType<ServiceGetAllResponse<Array<PoktanDataType>>> => ({
  queryKey: POKTAN_QUERY_KEY(params),
  queryFn: async () => {
    try {
      const response = await api.get<
        ServiceGetAllResponse<Array<PoktanDataType>>
      >('/kelembagaan/poktan', {
        params
      })

      return response.data
    } catch (error) {
      throw handleAxiosError(error)
    }
  }
})

const getAllPoktanByPetani = (
  params?: FilterPoktanValues & { id_petani?: string }
): ServiceQueryOptionType<ServiceGetAllResponse<Array<PoktanDataType>>> => ({
  queryKey: POKTAN_QUERY_KEY({ ...params, type: 'poktan-by-petani' }),
  queryFn: async () => {
    try {
      const response = await api.get<
        ServiceGetAllResponse<Array<PoktanDataType>>
      >(`/kelembagaan/petani/${params?.id_petani}/poktan`, {
        params: {
          page: params?.page,
          per_page: params?.per_page
        }
      })

      return response.data
    } catch (error) {
      throw handleAxiosError(error)
    }
  }
})

const getAllAnggotaPoktan = (
  params?: FilterPoktanValues
): ServiceQueryOptionType<
  ServiceGetAllResponse<Array<PoktanAnggotaDataType>>
> => ({
  queryKey: POKTAN_QUERY_KEY({ ...params, type: 'anggota-poktan' }),
  queryFn: async () => {
    try {
      const response = await api.get<
        ServiceGetAllResponse<Array<PoktanAnggotaDataType>>
      >(`/kelembagaan/poktan/${params?.id_poktan}/anggota`, {
        params: {
          ...params,
          nik: params?.nik || undefined
        }
      })

      return response.data
    } catch (error) {
      throw handleAxiosError(error)
    }
  }
})

const getAllKomoditasPoktan = (
  params?: FilterPoktanValues
): ServiceQueryOptionType<
  ServiceGetAllResponse<Array<KomoditasPoktanDataType>>
> => ({
  queryKey: POKTAN_QUERY_KEY({ ...params, type: 'komoditas-poktan' }),
  queryFn: async () => {
    try {
      const response = await api.get<
        ServiceGetAllResponse<Array<KomoditasPoktanDataType>>
      >(`/kelembagaan/poktan/${params?.id_poktan}/komoditas`, {
        params: {
          ...params,
          nik: params?.nik || undefined
        }
      })

      return response.data
    } catch (error) {
      throw handleAxiosError(error)
    }
  }
})

const getAllKomoditasAnggotaPoktan = (
  params?: FilterPoktanValues
): ServiceQueryOptionType<ServiceGetAllResponse<Array<KomoditasAnggotaPoktanItemType>>> => ({
  queryKey: POKTAN_QUERY_KEY({ ...params, type: 'komoditas-poktan-anggota' }),
  queryFn: async () => {
    try {
      const response = await api.get<
        ServiceGetAllResponse<Array<KomoditasAnggotaPoktanItemType>>
      >(
        `/kelembagaan/poktan/${params?.id_poktan}/anggota/${params?.id_anggota_poktan}/komoditas`,
        {
          params
        }
      )

      return response.data
    } catch (error) {
      throw handleAxiosError(error)
    }
  }
})

const getAllJenisKelompokPoktan = (
  params?: FilterPoktanValues
): ServiceQueryOptionType<
  ServiceGetAllResponse<Array<JenisKelompokPoktanDataType>>
> => ({
  queryKey: POKTAN_QUERY_KEY({ ...params, type: 'jenis-kelompok' }),
  queryFn: async () => {
    try {
      const response = await api.get<
        ServiceGetAllResponse<Array<JenisKelompokPoktanDataType>>
      >(`/kelembagaan/poktan/${params?.id_poktan}/jenis-kelompok`, {
        params: {
          ...params,
          nik: params?.nik || undefined
        }
      })

      return response.data
    } catch (error) {
      throw handleAxiosError(error)
    }
  }
})

const getAllKelasKemampuanPoktan = (
  params?: FilterPoktanValues
): ServiceQueryOptionType<
  ServiceGetAllResponse<Array<KelasKemampuanDataType>>
> => ({
  queryKey: POKTAN_QUERY_KEY({ ...params, type: 'kelas-kemampuan' }),
  queryFn: async () => {
    try {
      const response = await api.get<
        ServiceGetAllResponse<Array<KelasKemampuanDataType>>
      >(`/kelembagaan/poktan/${params?.id_poktan}/kelas-kemampuan`, {
        params: {
          ...params,
          nik: params?.nik || undefined
        }
      })

      return response.data
    } catch (error) {
      throw handleAxiosError(error)
    }
  }
})

const getById = (
  id: string
): ServiceQueryOptionType<ServiceGetByIdResponse<PoktanDataType>> => ({
  queryKey: POKTAN_QUERY_KEY(id),
  queryFn: async () => {
    try {
      const response = await api.get<ServiceGetByIdResponse<PoktanDataType>>(
        `/kelembagaan/poktan/${id}`
      )
      return response.data
    } catch (error) {
      throw handleAxiosError(error)
    }
  }
})

const getByIdAnggotaPoktan = (
  id: string,
  id_anggota: string
): ServiceQueryOptionType<ServiceGetByIdResponse<PoktanAnggotaDataType>> => ({
  queryKey: POKTAN_QUERY_KEY({ id, id_anggota }),
  queryFn: async () => {
    try {
      const response = await api.get<
        ServiceGetByIdResponse<PoktanAnggotaDataType>
      >(`/kelembagaan/poktan/${id}/anggota/${id_anggota}`)
      return response.data
    } catch (error) {
      throw handleAxiosError(error)
    }
  }
})

const create = (): ServiceMutationOptionType<
  ServiceGetByIdResponse<PoktanDataType>,
  MutationPoktanValues
> => ({
  mutationFn: async (data: MutationPoktanValues) => {
    try {
      const formData = new FormData()
      formData.append('nama_poktan', data.nama_poktan)
      formData.append('tahun_pembentukan', data.tahun_pembentukan.toString())
      formData.append('id_desa', data.id_desa.value)
      formData.append('alamat', data.alamat)
      if (data.latitude != null)
        formData.append('latitude', data.latitude.toString())
      if (data.longitude != null)
        formData.append('longitude', data.longitude.toString())
      ;(data.anggota || []).forEach((anggota, i) => {
        formData.append(`anggota[${i}][id_petani]`, anggota.id_petani)
        formData.append(`anggota[${i}][jenis]`, anggota.jenis)
        ;(anggota.lahan_id || []).forEach(lahan => {
          formData.append(`anggota[${i}][lahan_id][]`, lahan)
        })
      })
      if (data.id_jenis_dokumen_pembentukan)
        formData.append(
          'id_jenis_dokumen_pembentukan',
          data.id_jenis_dokumen_pembentukan
        )
      if (data.no_sk_pengukuhan)
        formData.append('no_sk_pengukuhan', data.no_sk_pengukuhan)
      if (data.penanda_tangan_pembentukan)
        formData.append(
          'penanda_tangan_pembentukan',
          data.penanda_tangan_pembentukan
        )
      if (data.dokumen_pembentukan instanceof File)
        formData.append('dokumen_pembentukan', data.dokumen_pembentukan)

      const response = await api.post<ServiceGetByIdResponse<PoktanDataType>>(
        '/kelembagaan/poktan',
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      )
      return response.data
    } catch (error) {
      throw handleAxiosError(error)
    }
  }
})

const createKomoditasAnggotaPoktan = (): ServiceMutationOptionType<
  ServiceGetByIdResponse<KomoditasPoktanDataType>,
  AddComodityMemberValues
> => ({
  mutationFn: async (data: AddComodityMemberValues) => {
    try {
      const response = await api.post<
        ServiceGetByIdResponse<KomoditasPoktanDataType>
      >(
        `/kelembagaan/poktan/${data?.poktan_id}/anggota/${data?.anggota_poktan_id}/komoditas`,
        {
          ...data,
          komoditas: data.komoditas.map(komoditas => ({
            id: komoditas.id?.value || '',
            volume: komoditas.volume ? Number.parseFloat(komoditas.volume) : 0
          }))
        }
      )
      return response.data
    } catch (error) {
      throw handleAxiosError(error)
    }
  }
})

const createAnggotaPoktan = (): ServiceMutationOptionType<
  ServiceGetByIdResponse<KomoditasPoktanDataType>,
  MutationAnggotaPoktanValues
> => ({
  mutationFn: async (data: MutationAnggotaPoktanValues) => {
    try {
      const response = await api.post<
        ServiceGetByIdResponse<KomoditasPoktanDataType>
      >(`/kelembagaan/poktan/${data?.id_poktan}/anggota`, {
        ...data,
        jenis: data.jenis ? data.jenis.toUpperCase() : null,
        id_lahan: data.lahan_id ?? []
      })
      return response.data
    } catch (error) {
      throw handleAxiosError(error)
    }
  }
})

const createKomoditasPoktan = (): ServiceMutationOptionType<
  ServiceGetByIdResponse<KomoditasPetaniDataType>,
  AddComodityPoktanValues
> => ({
  mutationFn: async (data: AddComodityPoktanValues) => {
    try {
      const response = await api.post<
        ServiceGetByIdResponse<KomoditasPetaniDataType>
      >(`/kelembagaan/poktan/${data.poktan_id}/komoditas`, {
        ...data,
        komoditas: data.komoditas.map(komoditas => ({
          id: komoditas.id?.value || undefined,
          volume: komoditas.volume
        }))
      })
      return response.data
    } catch (error) {
      throw handleAxiosError(error)
    }
  }
})

const createJenisKelompokPoktan = (): ServiceMutationOptionType<
  ServiceGetByIdResponse<KomoditasPetaniDataType>,
  { id_jenis_kelompok: string; poktan_id: string }
> => ({
  mutationFn: async (data: {
    id_jenis_kelompok: string
    poktan_id: string
  }) => {
    try {
      const response = await api.post<
        ServiceGetByIdResponse<KomoditasPetaniDataType>
      >(`/kelembagaan/poktan/${data.poktan_id}/jenis-kelompok`, {
        ...data
      })
      return response.data
    } catch (error) {
      throw handleAxiosError(error)
    }
  }
})

const createKelasKemampuanPoktan = (): ServiceMutationOptionType<
  ServiceGetByIdResponse<KelasKemampuanDataType>,
  CreateAbilityClassValues
> => ({
  mutationFn: async (data: CreateAbilityClassValues) => {
    try {
      const response = await api.post<
        ServiceGetByIdResponse<KelasKemampuanDataType>
      >(`/kelembagaan/poktan/${data.poktan_id}/kelas-kemampuan`, {
        ...data,
        id_kelas: data.id_kelas?.value
      })
      return response.data
    } catch (error) {
      throw handleAxiosError(error)
    }
  }
})

const update = (): ServiceMutationOptionType<
  ServiceGetByIdResponse<PetaniDataType>,
  MutationPoktanValues
> => ({
  mutationFn: async (data: MutationPoktanValues) => {
    try {
      const formData = new FormData()
      formData.append('_method', 'PUT')
      formData.append('nama_poktan', data.nama_poktan)
      formData.append('tahun_pembentukan', data.tahun_pembentukan.toString())
      formData.append('id_desa', data.id_desa.value)
      formData.append('alamat', data.alamat)
      if (data.latitude != null)
        formData.append('latitude', data.latitude.toString())
      if (data.longitude != null)
        formData.append('longitude', data.longitude.toString())
      if (data.id_jenis_dokumen_pembentukan)
        formData.append(
          'id_jenis_dokumen_pembentukan',
          data.id_jenis_dokumen_pembentukan
        )
      if (data.no_sk_pengukuhan)
        formData.append('no_sk_pengukuhan', data.no_sk_pengukuhan)
      if (data.penanda_tangan_pembentukan)
        formData.append(
          'penanda_tangan_pembentukan',
          data.penanda_tangan_pembentukan
        )
      if (data.dokumen_pembentukan instanceof File)
        formData.append('dokumen_pembentukan', data.dokumen_pembentukan)

      const response = await api.post<ServiceGetByIdResponse<PetaniDataType>>(
        `/kelembagaan/poktan/${data.id_poktan}`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      )
      return response.data
    } catch (error) {
      throw handleAxiosError(error)
    }
  }
})

const updateAnggotaPoktan = (): ServiceMutationOptionType<
  ServiceGetByIdResponse<PoktanAnggotaDataType>,
  MutationAnggotaPoktanValues
> => ({
  mutationFn: async (data: MutationAnggotaPoktanValues) => {
    try {
      const response = await api.put<
        ServiceGetByIdResponse<PoktanAnggotaDataType>
      >(
        `/kelembagaan/poktan/${data?.id_poktan}/anggota/${data?.id_anggota_poktan}`,
        {
          ...data,
          jenis: data.jenis ? data.jenis.toUpperCase() : null,
          id_lahan: data.lahan_id ?? []
        }
      )
      return response.data
    } catch (error) {
      throw handleAxiosError(error)
    }
  }
})

const updateKelasKemampuanPoktan = (): ServiceMutationOptionType<
  ServiceGetByIdResponse<KelasKemampuanDataType>,
  CreateAbilityClassValues
> => ({
  mutationFn: async (data: CreateAbilityClassValues) => {
    try {
      const response = await api.put<
        ServiceGetByIdResponse<KelasKemampuanDataType>
      >(
        `/kelembagaan/poktan/${data.poktan_id}/kelas-kemampuan/${data.ability_class_id}`,
        {
          ...data,
          id_kelas: data.id_kelas?.value
        }
      )
      return response.data
    } catch (error) {
      throw handleAxiosError(error)
    }
  }
})

const getAllPoktanInfinite: (
  params?: Record<string, any>
) => InfiniteQueryOptions = (params?: Record<string, any>) => ({
  queryKey: POKTAN_QUERY_KEY(params),
  initialPageParam: 1,
  getNextPageParam: (lastPage: ServiceGetAllResponse<PoktanDataType[]>) => {
    const meta = lastPage.meta

    if (!meta) return undefined
    return (meta?.current_page ?? 0) < (meta?.last_page ?? 0)
      ? (meta?.current_page ?? 0) + 1
      : undefined
  },
  queryFn: async ({ pageParam }: { pageParam: number }) => {
    try {
      const response = await api.get<ServiceGetAllResponse<PoktanDataType[]>>(
        `/kelembagaan/poktan`,
        {
          params: {
            ...params,
            page: pageParam
          }
        }
      )

      return {
        message: response.data.message,
        meta: response.data.meta,
        data: response.data.data
      }
    } catch (error) {
      throw handleAxiosError(error)
    }
  }
})

const deleteData = (): ServiceMutationOptionType<
  ServiceGetByIdResponse<PoktanDataType>,
  { id_poktan: string }
> => ({
  mutationFn: async (data: { id_poktan: string }) => {
    try {
      const response = await api.delete<ServiceGetByIdResponse<PoktanDataType>>(
        `/kelembagaan/poktan/${data.id_poktan}`
      )
      return response.data
    } catch (error) {
      throw handleAxiosError(error)
    }
  }
})

const deleteDataAnggotaPoktan = (): ServiceMutationOptionType<
  ServiceGetByIdResponse<PoktanAnggotaDataType>,
  { id_poktan: string; id_data: string }
> => ({
  mutationFn: async (data: { id_poktan: string; id_data: string }) => {
    try {
      const response = await api.delete<
        ServiceGetByIdResponse<PoktanAnggotaDataType>
      >(`/kelembagaan/poktan/${data?.id_poktan}/anggota/${data?.id_data}`)
      return response.data
    } catch (error) {
      throw handleAxiosError(error)
    }
  }
})

const deleteDataKomoditasPoktan = (): ServiceMutationOptionType<
  ServiceGetByIdResponse<KomoditasPoktanDataType>,
  { id_poktan: string; id_data: string }
> => ({
  mutationFn: async (data: { id_poktan: string; id_data: string }) => {
    try {
      const response = await api.delete<
        ServiceGetByIdResponse<KomoditasPoktanDataType>
      >(`/kelembagaan/poktan/${data?.id_poktan}/komoditas/${data?.id_data}`)
      return response.data
    } catch (error) {
      throw handleAxiosError(error)
    }
  }
})

const deleteDataJenisKelompokPoktan = (): ServiceMutationOptionType<
  ServiceGetByIdResponse<JenisKelompokPoktanDataType>,
  { id_poktan: string; id_data: string }
> => ({
  mutationFn: async (data: { id_poktan: string; id_data: string }) => {
    try {
      const response = await api.delete<
        ServiceGetByIdResponse<JenisKelompokPoktanDataType>
      >(
        `/kelembagaan/poktan/${data?.id_poktan}/jenis-kelompok/${data?.id_data}`
      )
      return response.data
    } catch (error) {
      throw handleAxiosError(error)
    }
  }
})

const deleteDataKelasKemampuanPoktan = (): ServiceMutationOptionType<
  ServiceGetByIdResponse<KelasKemampuanDataType>,
  { id_poktan: string; id_data: string }
> => ({
  mutationFn: async (data: { id_poktan: string; id_data: string }) => {
    try {
      const response = await api.delete<
        ServiceGetByIdResponse<KelasKemampuanDataType>
      >(
        `/kelembagaan/poktan/${data?.id_poktan}/kelas-kemampuan/${data?.id_data}`
      )
      return response.data
    } catch (error) {
      throw handleAxiosError(error)
    }
  }
})

const getRiwayatKodePoktan = (
  params: { id_poktan: string; page?: number; per_page?: number }
): ServiceQueryOptionType<
  ServiceGetAllResponse<Array<RiwayatKodePoktanDataType>>
> => ({
  queryKey: POKTAN_QUERY_KEY({ ...params, type: 'riwayat-kode' }),
  queryFn: async () => {
    try {
      const response = await api.get<
        ServiceGetAllResponse<Array<RiwayatKodePoktanDataType>>
      >(`/kelembagaan/poktan/${params.id_poktan}/code-logs`, {
        params: {
          page: params.page,
          per_page: params.per_page
        }
      })
      return response.data
    } catch (error) {
      throw handleAxiosError(error)
    }
  }
})

const exportData = (params?: FilterPoktanValues) => ({
  mutationFn: async () => {
    try {
      const response = await api.get<ExportResponse>(
        `/kelembagaan/poktan/export`,
        { params }
      )
      return response.data
    } catch (error) {
      throw handleAxiosError(error)
    }
  }
})

export const exportAnggotaData = (poktan_id: string) => ({
  mutationFn: async (): Promise<ExportResponse> => {
    try {
      const response = await api.get<ExportResponse>(
        `/kelembagaan/poktan/${poktan_id}/anggota/export`
      )

      return response.data
    } catch (error) {
      throw handleAxiosError(error)
    }
  }
})

export const daftarPoktan = {
  queryKey: [POKTAN_QUERY_KEY(undefined)[0]],
  getAll,
  getAllPoktanInfinite,
  getAllPoktanByPetani,
  deleteData,
  create,
  getById,
  createAnggotaPoktan,
  createKomoditasPoktan,
  getAllAnggotaPoktan,
  getAllKomoditasPoktan,
  deleteDataKomoditasPoktan,
  getAllJenisKelompokPoktan,
  deleteDataJenisKelompokPoktan,
  getAllKelasKemampuanPoktan,
  deleteDataKelasKemampuanPoktan,
  update,
  getByIdAnggotaPoktan,
  getAllKomoditasAnggotaPoktan,
  createKomoditasAnggotaPoktan,
  updateAnggotaPoktan,
  deleteDataAnggotaPoktan,
  createJenisKelompokPoktan,
  createKelasKemampuanPoktan,
  updateKelasKemampuanPoktan,
  exportData,
  exportAnggotaData,
  getRiwayatKodePoktan
}
